import * as React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';

import Demo, { DemoSliderControl } from '@/components/Demo';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';

import Ball from './FadeInBall';
import CheckmarkButton from './CheckmarkButton';
import { constructSnippet } from './ToTargetOpacityDemo.helpers';

interface Props {
  codeOutput: React.ReactNode;
  initialIsToggled?: boolean;
  initialTargetOpacity?: number;
}

function ToTargetOpacityDemo({
  codeOutput,
  initialIsToggled = false,
  initialTargetOpacity = 0.5,
}: Props) {
  const [isToggled, setIsToggled] = React.useState(initialIsToggled);

  return (
    <OuterWrapper>
      <Demo
        initialValues={{
          targetOpacity: initialTargetOpacity,
        }}
        outputWrapper={null}
        controlPosition="top"
        customGrid={{
          gridTemplateColumns: 'min(400px, 100%)',
          justifyContent: 'center',
        }}
        controls={[
          <DemoSliderControl
            key="targetOpacity"
            id="targetOpacity"
            label="Target Opacity"
            min={0.1}
            max={1}
            step={0.1}
          />,
        ]}
      >
        {({ targetOpacity }) => {
          const codeSnippet = constructSnippet(
            targetOpacity,
            isToggled
          );

          return (
            <Wrapper>
              <Demonstration data-can-be-annotated="false">
                <BallWrapper>
                  <Ball
                    targetOpacity={targetOpacity}
                    isToggled={isToggled}
                  />
                </BallWrapper>
                <Button
                  isToggled={isToggled}
                  onClick={() => setIsToggled(!isToggled)}
                >
                  Render element
                </Button>
              </Demonstration>
              <SnippetWrapper>
                <CodeSnippetProvider lang="html" code={codeSnippet}>
                  {codeOutput}
                </CodeSnippetProvider>
              </SnippetWrapper>
            </Wrapper>
          );
        }}
      </Demo>
    </OuterWrapper>
  );
}

// On larger windows, we want the text in the code snippet to line up with the body paragraphs:
const OuterWrapper = styled.div`
  @media (min-width: 50rem) {
    margin-inline: -16px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  flex-wrap: wrap;
  gap: 16px;

  @media ${BREAKPOINTS.smAndSmaller} {
    flex-direction: column;
  }

  @media ${BREAKPOINTS.smAndLarger} {
    margin: -16px;
    margin-top: 0;
  }
`;

const SnippetWrapper = styled.div`
  --code-font-size: 0.875rem;
  --code-line-height: 1.325rem;
  flex: 2;
  width: 26.875rem;
  min-width: 26.875rem;
  min-height: 21.25rem;
  border: 1px solid var(--color-gray-300);
  border-radius: 8px;
  text-align: left;
  white-space: pre-wrap;

  @media ${BREAKPOINTS.smAndSmaller} {
    width: 100%;
    min-width: 0;
    min-height: 0;
    max-width: 100%;
    overflow-x: auto;
  }
`;

const Demonstration = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 32px;
  min-width: 14rem;
  padding: 16px;
  border: 1px solid var(--color-gray-300);
  border-radius: 8px;

  @media ${BREAKPOINTS.smAndSmaller} {
    gap: 16px;
    padding-block: 24px;
  }
`;

const BallWrapper = styled.div`
  flex: 1;
  display: grid;
  place-content: center;
`;

const Button = styled(CheckmarkButton)`
  width: 11rem;
  height: 3rem;
  padding: 0;
`;

export default ToTargetOpacityDemo;
